import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { api, listEnrollments, listInventoryRuns } from "../api/rest";
import { useSiteId } from "../hooks/useSiteId";
import { useWarehouseId } from "../hooks/useWarehouseId";
import { useTelemetryStore } from "../stores/telemetryStore";
import { buildKitRows, coarseAge, kitOnline, type KitRow } from "./inventoryKits";
import styles from "./FleetPage.module.css";
import css from "./InventoryKitsPage.module.css";

/**
 * ADR 365 — the kits of the active site as an operations list: enrollment,
 * heartbeat, kit socket + localization, run phase, upload queue, last upload,
 * last count. Approve/revoke stays on /admin/devices (site-admin); this page is
 * for the person who runs the counts.
 */
export function InventoryKitsPage() {
  const { t, i18n } = useTranslation();
  const siteId = useSiteId();
  const warehouseId = useWarehouseId();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 5_000);
    return () => clearInterval(id);
  }, []);
  const enrollments = useQuery({ queryKey: ["enrollments"], queryFn: () => listEnrollments(), refetchInterval: 15_000 });
  const assets = useQuery({ queryKey: ["assets", siteId], queryFn: () => api.listAssets(siteId!), enabled: !!siteId });
  const runs = useQuery({ queryKey: ["inventory-runs", siteId], queryFn: () => listInventoryRuns(siteId!), enabled: !!siteId });
  const gateways = useTelemetryStore((s) => s.gateways);
  const rows = useMemo(
    () => buildKitRows(enrollments.data ?? [], assets.data ?? [], gateways, runs.data ?? [], warehouseId),
    [enrollments.data, assets.data, gateways, runs.data, warehouseId],
  );
  const loading = enrollments.isPending || assets.isPending || runs.isPending;

  return (
    <div className={styles.page}>
      <div className={css.head}>
        <h1 className={styles.h1} style={{ margin: 0 }}>{t("kits.title")}</h1>
        <p className={css.sub}>{t("kits.subtitle")}</p>
      </div>
      <div className={styles.tableScroll}>
        <table className={styles.table} aria-label={t("kits.title")}>
          <thead>
            <tr>
              <th>{t("kits.col.kit")}</th>
              <th>{t("kits.col.enrollment")}</th>
              <th>{t("kits.col.heartbeat")}</th>
              <th>{t("kits.col.kitLink")}</th>
              <th>{t("kits.col.phase")}</th>
              <th>{t("kits.col.queue")}</th>
              <th>{t("kits.col.lastUpload")}</th>
              <th>{t("kits.col.lastRun")}</th>
            </tr>
          </thead>
          <tbody>
            {loading
              ? <tr><td colSpan={8}><span className="skeleton" style={{ width: 240, height: 14, display: "block" }} /></td></tr>
              : rows.length === 0
                ? <tr><td colSpan={8} className={styles.empty}>{t("kits.empty")} <Link to="/admin/devices">{t("kits.emptyAction")}</Link></td></tr>
                : rows.map((r) => <KitTr key={r.hardwareId} row={r} now={now} t={t} locale={i18n.language} />)}
          </tbody>
        </table>
      </div>
    </div>
  );
}

type T = (key: string, opts?: Record<string, unknown>) => string;

function KitTr({ row, now, t, locale }: { row: KitRow; now: number; t: T; locale: string }) {
  const online = kitOnline(row.gateway, now);
  const ic3 = row.gateway?.ic3 ?? null;
  const enrollmentPill = row.enrollment.status === "approved" ? styles.pill_loaded
    : row.enrollment.status === "pending" ? styles.pill_idle : styles.pill_offline;
  return (
    <tr>
      <td>
        <div className={css.kitName}>{row.asset?.label ?? row.hardwareId}</div>
        <div className={`${styles.mono} ${css.kitId}`}>{row.hardwareId}</div>
      </td>
      <td>
        <span className={`${styles.pill} ${enrollmentPill}`}>{t(`kits.enrollment.${row.enrollment.status}`)}</span>
        {row.enrollment.status !== "approved" && <> <Link to="/admin/devices">{t("kits.manage")}</Link></>}
      </td>
      <td>
        <span className={`${styles.pill} ${online === "online" ? styles.pill_loaded : online === "stale" ? styles.pill_offline : styles.pill_empty}`}>
          {t(`kits.online.${online}`)}
        </span>
        {row.gateway && <div className={css.hint}>{t("kits.seenAgo", { age: coarseAge(now - row.gateway.lastSeenMs, locale) })}</div>}
      </td>
      <td>
        {ic3 ? (
          <>
            <span className={`${styles.pill} ${ic3.kitWsOk ? styles.pill_loaded : styles.pill_offline}`}>
              {t(ic3.kitWsOk ? "kits.ws.ok" : "kits.ws.down")}
            </span>
            <div className={css.hint}>{t(ic3.localized ? "kits.localized" : "kits.notLocalized")}</div>
          </>
        ) : <span className={css.dim}>—</span>}
      </td>
      <td>{ic3 ? t(`kits.phase.${ic3.runPhase}`, { defaultValue: ic3.runPhase }) : <span className={css.dim}>—</span>}</td>
      <td className={styles.mono}>
        {ic3 ? (
          ic3.outboxPending === 0
            ? t("kits.queue.empty")
            : t("kits.queue.pending", { count: ic3.outboxPending, age: ic3.outboxOldestAgeS === null ? "—" : coarseAge(ic3.outboxOldestAgeS * 1000, locale) })
        ) : <span className={css.dim}>—</span>}
      </td>
      <td>
        {ic3?.lastUploadError
          ? <span className={css.error} title={ic3.lastUploadError}>{t("kits.upload.error")}</span>
          : ic3?.lastUploadOkTs
            ? <span className={styles.mono}>{t("kits.seenAgo", { age: coarseAge(now - ic3.lastUploadOkTs, locale) })}</span>
            : <span className={css.dim}>—</span>}
      </td>
      <td>
        {row.lastRun
          ? <Link to={`/inventory/runs/${row.lastRun.id}`}>
              {row.lastRun.name ?? row.lastRun.id.slice(0, 8)}
              {row.lastRun.startedAt && <span className={css.hint}> · {new Date(row.lastRun.startedAt).toLocaleDateString(locale)}</span>}
            </Link>
          : <span className={css.dim}>{t("kits.noRun")}</span>}
      </td>
    </tr>
  );
}
